/**
 * **A goal's measured quantity, as a card prints it** — `85 kg`, `12` where the goal carries no
 * unit, `null` where nobody has measured it.
 *
 * ── Why it is here rather than in either page's `model/` ──────────────────────────────────────
 * The same three figures — start, now, target — are drawn by the client detail's goal card and by
 * the roster preview panel's, and each had grown its own formatter. Two spellings of one rule, with
 * nothing comparing them, and they had already parted on the half that matters:
 *
 * · The detail translated the unit with no fallback, so a unit this build has no word for printed
 *   the raw i18n key — `web:clientDetail.goal.unit.Steps` — in the middle of a card.
 * · The panel passed `defaultValue`, so the same goal printed `12 Steps` there.
 *
 * The wire's `TargetUnit` is `Kg | Reps | Sessions` today, so neither is visible against real data;
 * it is a field the backend can add a member to without breaking a client, which is exactly the
 * change that would have shipped one screen printing a key and another printing a word. The
 * fallback is the panel's, because a unit we cannot name is still information.
 *
 * ── What it deliberately does NOT decide ──────────────────────────────────────────────────────
 * What an absent measurement LOOKS like. It answers `null`, and the two cards say different things
 * about that on purpose: the detail drops the figure from its list, the panel prints an em dash to
 * hold the column. Folding that in would have made this a component's decision wearing a model's
 * signature.
 *
 * Pure: no React, no DOM, no query hook, no i18next import. `t` and `locale` arrive as parameters,
 * so a test reads this in one language without a provider.
 */

export interface GoalFigureDeps {
  /** i18next's `t`, narrowed to what this needs: a key, and a default for a unit we cannot name. */
  readonly t: (key: string, options?: { readonly defaultValue?: string }) => string;
  /** The trainer's locale — a decimal comma in Polish, a point in English. */
  readonly locale: string;
}

/**
 * At most one decimal place.
 *
 * The wire sends a `double`, and a bodyweight goal arrives as `82.5`; printing it in full would put
 * `82.4000000000000057` on a card. One place is what every goal in this product is measured to.
 */
const MAX_FRACTION_DIGITS = 1;

/**
 * `value` with its unit, or `null` where the goal has no measurement of that kind.
 *
 * The unit arrives as the contract's ENUM NAME (`Kg`, `Reps`, `Sessions`), never as a word, so it
 * is translated rather than printed — `Reps` in a Polish column would be the one English word on
 * the screen. A name this build has no key for falls back to the name itself.
 */
export function goalFigureText(
  value: number | null | undefined,
  unit: string | null | undefined,
  { t, locale }: GoalFigureDeps,
): string | null {
  if (value == null) return null;
  const number = new Intl.NumberFormat(locale, {
    maximumFractionDigits: MAX_FRACTION_DIGITS,
  }).format(value);
  if (unit == null) return number;
  return `${number} ${t(`web:clientDetail.goal.unit.${unit}`, { defaultValue: unit })}`;
}
